import { useEffect, useState } from "react";
import { Lock, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { portalAuth, portalApi } from "@/lib/portal";

const PortalGate = ({ onAuthed, mode = "portal" }) => {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isAdminMode = mode === "admin";
  const endpoint = isAdminMode ? "/portal/admin/login" : "/portal/login";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error("Please enter the access password.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await portalApi.post(endpoint, { password });
      portalAuth.setSession(data.token, data.role);
      toast.success(isAdminMode ? "Welcome, Admin." : "Access granted.");
      onAuthed?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Authentication failed";
      toast.error(typeof detail === "string" ? detail : "Authentication failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid={isAdminMode ? "portal-admin-gate" : "portal-gate"}
      className="min-h-[calc(100vh-6rem)] bg-[#F5F5DC] flex items-center"
    >
      <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <p className="overline mb-6 fade-up">
            {isAdminMode ? "Admin Console" : "Restricted Access"}
          </p>
          <h1 className="font-display text-5xl lg:text-7xl text-[#111111] leading-[1.05] fade-up-delay-1">
            CEO Capability
            <br />
            <span className="text-[#8B0000]">
              {isAdminMode ? "Admin Console." : "Building Portal."}
            </span>
          </h1>
          <p className="mt-6 font-display italic text-2xl text-[#8B0000] fade-up-delay-2">
            {isAdminMode
              ? "Manage your premium capability building library."
              : "Exclusive SAP, AI, Analytics, and Enterprise Technology Capability Building by Anila Consulting Leadership."}
          </p>
          <p className="mt-6 text-[#4A4A4A] leading-relaxed max-w-xl fade-up-delay-3">
            {isAdminMode
              ? "Enter your administrator passphrase to upload, edit, or remove capability building videos."
              : "This is a private learning environment. Enter the access passphrase provided to you by Anila Consulting to begin."}
          </p>
        </div>

        <div className="lg:col-span-5 fade-up-delay-3">
          <form
            data-testid={isAdminMode ? "admin-login-form" : "portal-login-form"}
            onSubmit={handleSubmit}
            className="bg-white p-10 border border-[#E5E5E5]"
          >
            <div className="w-12 h-12 bg-[#8B0000]/10 flex items-center justify-center mb-6">
              {isAdminMode ? (
                <ShieldCheck size={22} className="text-[#8B0000]" />
              ) : (
                <Lock size={22} className="text-[#8B0000]" />
              )}
            </div>
            <h2 className="font-display text-2xl text-[#111111] mb-2">
              {isAdminMode ? "Administrator sign-in" : "Enter the portal"}
            </h2>
            <p className="text-sm text-[#4A4A4A] mb-8">
              {isAdminMode
                ? "Admin credentials are issued to portal owners only."
                : "Your passphrase was shared with you privately."}
            </p>

            <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#111111] mb-2 block">
              {isAdminMode ? "Admin passphrase" : "Access passphrase"}
            </label>
            <input
              data-testid={isAdminMode ? "admin-password-input" : "portal-password-input"}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="••••••••"
              className="w-full border border-[#E5E5E5] px-4 py-3 bg-white text-[#111111] focus:border-[#8B0000] transition-colors mb-6"
            />

            <button
              type="submit"
              disabled={submitting}
              data-testid={isAdminMode ? "admin-login-button" : "portal-login-button"}
              className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting
                ? isAdminMode
                  ? "Verifying..."
                  : "Unlocking..."
                : isAdminMode
                ? "Sign in as Admin"
                : "Unlock Portal"}{" "}
              {!submitting && <ArrowRight size={16} />}
            </button>

            <p className="text-xs text-[#4A4A4A] mt-6 leading-relaxed">
              Sessions are encrypted and expire automatically after 12 hours of
              inactivity. Unauthorised access attempts are logged.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default PortalGate;
